/**
 * Per-campaign access grants (RBAC).
 *
 * Owners/super-admins (unrestricted) and admin-level grant-holders manage who
 * can access a campaign and at what level. Backend gates these routes at
 * campaign:grant:* (admin level); see internal/campaigns/grants_handler.go.
 */
import { apiClient } from '@discovr/common';
import type { DataResponse } from '@discovr/common';

export type GrantLevel = 'read' | 'write' | 'admin';

/** Shared label/description map for a grant level — single source of truth
 *  for AccessControlTab, the invite picker, and the member drawer. */
export const GRANT_LEVEL_LABEL: Record<GrantLevel, string> = { read: 'Viewer', write: 'Editor', admin: 'Admin' };
export const GRANT_LEVEL_DESCRIPTION: Record<GrantLevel, string> = {
  read: 'Can view the campaign and chat with the team',
  write: 'Can view, approve, upload, and send briefs',
  admin: 'Can also manage who has access',
};

export interface CampaignGrant {
  user_id: string;
  email?: string;
  display_name?: string;
  /** The granted user's role — places the grant in its lane (admin vs brand).
   *  Per-row seniority gating in Access Control was removed: any admin-level
   *  caller in their lane may manage any row in that lane. */
  role?: 'admin_member' | 'brand_member';
  level: GrantLevel;
  granted_at: string;
}

export const campaignGrants = {
  /** List a campaign's access grants (hydrated with member email/name). */
  list: (campaignId: string): Promise<CampaignGrant[]> =>
    apiClient
      .get<DataResponse<CampaignGrant[]>>(`/campaigns/${encodeURIComponent(campaignId)}/grants`)
      .then((r) => r.data.data),

  /** Assign or update a member's access level on a campaign (idempotent). */
  assign: (
    campaignId: string,
    body: { user_id: string; level: GrantLevel },
  ): Promise<CampaignGrant> =>
    apiClient
      .post<DataResponse<CampaignGrant>>(
        `/campaigns/${encodeURIComponent(campaignId)}/grants`,
        body,
      )
      .then((r) => r.data.data),

  /**
   * Revoke a member's access (204 No Content).
   * `body.confirm` carries the typed-confirmation value (ActionConfirmDialog's
   * `confirmText="CONFIRM"` gate) for the BE's confirm.Matches check. DELETE
   * has no body param on axios's shorthand, so it goes through `config.data`.
   */
  revoke: (campaignId: string, userId: string, body?: { confirm?: string }): Promise<void> =>
    apiClient
      .delete(
        `/campaigns/${encodeURIComponent(campaignId)}/grants/${encodeURIComponent(userId)}`,
        { data: body },
      )
      .then(() => undefined),
};
